import { AssignStatus } from "@/enum/list.enum";
import { ROUTE_CONFIG } from "@/routes/config";

import { Tabs } from "../pages/Unassigned/unassignedTabs";

/**
 * Which list-detail tab a lead opens on, keyed by the status the lead currently holds.
 * Mirrors the tab set the Leads view actually renders ({@link Tabs}); every status not
 * listed here belongs to a running or finished campaign (ASSIGNED, RUNNING, MESSAGE_SENT,
 * REPLIES, PAUSED, ENDED, DONE, …) and surfaces on the ASSIGNED tab.
 */
const LIST_TAB_BY_STATUS: Partial<Record<AssignStatus, Tabs>> = {
	[AssignStatus.DRAFT]: Tabs.UNASSIGNED,
	[AssignStatus.SAVED_FOR_LATER]: Tabs.SAVED_FOR_LATER,
	[AssignStatus.ARCHIVED]: Tabs.ARCHIVED,
	[AssignStatus.UNDER_REVIEW]: Tabs.UNDER_REVIEW,
	[AssignStatus.TRACKED_PROFILE]: Tabs.TRACKED_PROFILE,
};

/**
 * Which list-detail tab a lead is on. Shared by the deep-link builder below and the in-page
 * "jump to this lead" flow (`useReachOutColumnWiring`), so a link and a client-side jump can
 * never disagree about where the lead is.
 */
export const listDetailTabForStatus = (assignStatus?: AssignStatus): Tabs =>
	(assignStatus && LIST_TAB_BY_STATUS[assignStatus]) ?? Tabs.ASSIGNED;

export interface ListDetailLinkInput {
	listId: number;
	/** Seeds the Leads view's text search so the lead is the visible row on arrival. */
	prospectName?: string | null;
	/** Decides the tab. Omitted (or unmapped) lands on ASSIGNED. */
	assignStatus?: AssignStatus;
}

/**
 * Build a list-detail deep-link that lands on the tab the lead is actually on, with the
 * Leads view pre-searched for their name.
 *
 * The tab matters: the older hand-written form of this link hardcoded `tab=UNASSIGNED`, so a
 * lead who had replied (or was mid-campaign) opened a tab they are not on and the list read as
 * empty — the "link to the list is not working" of PREC-4028. Resolving the tab from the lead's
 * `assignStatus` is what makes the destination show the lead.
 */
export const buildListDetailLink = ({
	listId,
	prospectName,
	assignStatus,
}: ListDetailLinkInput): string => {
	const tab = listDetailTabForStatus(assignStatus);

	const params = new URLSearchParams({ tab });

	if (prospectName) {
		params.set("searchTerm", prospectName);
	}

	return `${ROUTE_CONFIG.LISTS_DETAIL}/${listId}?${params.toString()}`;
};
